import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersResponses";
import { getLocationLevelLabel } from "api-types/locations/locationDisplay";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { assert } from "common/utils/assertOfType";
import { validateZipCode } from "common/utils/formValidators";
import { isInputBlank } from "common/utils/isInputBlank";
import SalesStaffAccessWarning from "lib/components/builders/communities/SalesStaffAccessWarning";
import { useFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import { putLocation } from "lib/storage/locationStorage";
import getUserFullName from "lib/utils/userName";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";
import useAppContext from "ui/hooks/useAppContext";
import { Message } from "ui/text/Message";
import { getSalesStaffResourceFilterUrl, grantSalesStaffResourceViewAccess } from "../utils/salesStaffResourceAccess";
import type { LocationUpdateFields } from "./CommunityDetailsForm";
import CommunityDetailsForm from "./CommunityDetailsForm";

interface CommunityDetailsModalProps extends ModalProps {
  location: ClientLocationHydrated;
  onSuccess: (location: ClientLocationHydrated) => void;
}

export default function CommunityDetailsModal({ onClose, open, location, onSuccess }: CommunityDetailsModalProps) {
  const { builder, hasPermission } = useUserContext();
  const editableFields: Omit<LocationUpdateFields, "brochureSettings"> = {
    name: location.name,
    externalId: location.externalId,
    address: location.address,
    city: location.city,
    state: location.state,
    zip: location.zip,
    country: location.country,
    displayOptionPrices: location.displayOptionPrices,
    pricingVisibilitySettings: location.pricingVisibilitySettings,
    disableShowroomViewer: location.disableShowroomViewer,
    disableLots: location.disableLots,
    showMapTab: location.showMapTab,
    salesMapUseShowroomIntro: location.salesMapUseShowroomIntro,
    useOfficeAddress: location.useOfficeAddress,
    officeAddress: location.officeAddress,
    officeCity: location.officeCity,
    officeState: location.officeState,
    officeZip: location.officeZip,
    officeCountry: location.officeCountry,
    generalNotes: location.generalNotes,
    govtCodeAndStandards: location.govtCodeAndStandards,
    buildingCodeAnalysisDesignCriteria: location.buildingCodeAnalysisDesignCriteria,
    salesStaffId: location.salesStaff?.id || null,
    templateCode: location.templateCode,
  } as const;

  type EditableField = keyof typeof editableFields;

  function assertIsEditableField(s: string): asserts s is EditableField {
    assert(s in editableFields);
  }

  const optionalFields: EditableField[] = [
    "externalId",
    "displayOptionPrices",
    "disableLots",
    "showMapTab",
    "generalNotes",
    "govtCodeAndStandards",
    "pricingVisibilitySettings",
    "buildingCodeAnalysisDesignCriteria",
    "salesMapUseShowroomIntro",
    "salesStaffId",
    "templateCode",
    "officeAddress",
    "officeCity",
    "officeState",
    "officeZip",
    "officeCountry",
  ];

  const { setUserNotification } = useAppContext();
  const [locationFields, setCommunityFields] = useState(editableFields);

  const salesStaffPermission: ClientBuilderUserPermission = "ALLOWED_AS_SALES_STAFF";
  const { data: salesAgents, loading: salesAgentsLoading } = useFetch<ClientUserHydrated[]>(
    `${WEB_API_BASE_PATH}/builders/${location?.builderId}/users-internal?permission=${salesStaffPermission}`,
    [location?.builderId],
  );
  const resourceFilterUrl = getSalesStaffResourceFilterUrl({
    builderId: location.builderId,
    resourceType: "communities",
    resourceId: location.id,
  });
  const { data: usersWithLocationAccess, mutate: mutateUsersWithLocationAccess } = useFetch<ClientUserHydrated[]>(
    resourceFilterUrl ?? "",
    [location.builderId, location.id],
    { handleErrorLocally: true },
  );

  const usersWithLocationAccessSet = new Set((usersWithLocationAccess ?? []).map((u) => u.id));
  const eligibleSalesAgents = (salesAgents ?? []).filter((sa) => usersWithLocationAccessSet.has(sa.id));
  const selectedSalesStaff = (salesAgents ?? []).find((sa) => sa.id === locationFields.salesStaffId);
  const staleSalesStaffAssignment =
    !!selectedSalesStaff && locationFields.salesStaffId && !usersWithLocationAccessSet.has(locationFields.salesStaffId);

  const handleGrantViewAccessForSalesStaff = async () => {
    if (!selectedSalesStaff) {
      return;
    }

    try {
      await grantSalesStaffResourceViewAccess({
        builderId: location.builderId,
        targetUser: selectedSalesStaff,
        resourceType: "communities",
        resourceId: location.id,
      });
      await mutateUsersWithLocationAccess();
      setUserNotification({
        message: `Granted location view access to ${getUserFullName(selectedSalesStaff)}.`,
        messageType: "success",
      });
    } catch {
      setUserNotification({
        message: "Could not grant location access to selected sales staff.",
        messageType: "error",
      });
    }
  };

  // reset location fields on modal close / open
  useEffect(() => {
    setCommunityFields(editableFields);
  }, [open]);

  const handleSubmit = async () => {
    const [res, err] = await putLocation(location.builderId, location.id, { ...locationFields });

    if (err) {
      setUserNotification(err);
      return;
    }

    setUserNotification({ message: "Location details updated!", messageType: "success" });
    onSuccess(res);
    onClose?.();
  };

  const submittable =
    Object.keys(locationFields).filter((field) => {
      assertIsEditableField(field);
      if (field === "pricingVisibilitySettings") {
        return false;
      }
      if ((field === "zip" || field === "officeZip") && !isInputBlank(locationFields[field])) {
        return !!validateZipCode(locationFields[field], locationFields[field === "zip" ? "country" : "officeCountry"]);
      }
      return !optionalFields.includes(field) && isInputBlank(locationFields[field]);
    }).length === 0;

  if (salesAgentsLoading) {
    return null;
  }

  const locationLevel = getLocationLevelLabel(location, builder?.locationSettings);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Update ${locationLevel} Details`}
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          {" "}
          <Button btnSize="large" type="button" btnStyle="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button btnSize="large" type="submit" disabled={!submittable} form="community-details-modal">
            Submit
          </Button>
        </>
      }
    >
      <form
        id="community-details-modal"
        onSubmit={(e) => {
          e.preventDefault();
          if (submittable) {
            void handleSubmit();
          }
        }}
      >
        <Message marginBottom>
          <>Edit the details of your {locationLevel}.</>
        </Message>
        <div className="modal-body">
          <CommunityDetailsForm
            communityFields={locationFields}
            setCommunityFields={setCommunityFields}
            salesStaff={eligibleSalesAgents}
            locationLevel={locationLevel}
            salesStaffWarning={
              staleSalesStaffAssignment && selectedSalesStaff ? (
                <SalesStaffAccessWarning
                  assigneeName={getUserFullName(selectedSalesStaff)}
                  resourceLabel="location"
                  canGrantAccess={hasPermission("EDIT_STAFF_PERMISSIONS")}
                  onGrantAccess={handleGrantViewAccessForSalesStaff}
                  onRemoveAssignment={() => setCommunityFields({ ...locationFields, salesStaffId: null })}
                />
              ) : undefined
            }
          />
        </div>
      </form>
    </Modal>
  );
}
